import type { DetailRowData, ServiceIntervalMap, ServiceRecord } from '../types';

import { getUnitLabels } from 'pages/MaintenancePage/helpers/getUnitLabels';
import { getUsageUnit } from 'pages/MaintenancePage/helpers/getUsageUnit';

type DetailRowStrings = {
    CYCLES_UNIT: string;
    SERVICE_RECORD_INITIAL_USAGE: string;
    SERVICE_RECORD_NOTES: string;
    SERVICE_RECORD_PART_SERIAL_NUMBER: string;
    SERVICE_RECORD_PERFORMED_DATE: string;
    SERVICE_RECORD_USAGE_AT_SERVICE: string;
    UNIT_FLIGHTS_LOWER: string;
    UNIT_HOURS_SHORT: string;
};

// The labeled rows of the record detail body, in display order. Optional record
// fields (part serial, used-part baseline, notes) contribute a row only when present.
export const getDetailRows = ({
    record,
    serviceIntervals,
    strings
}: {
    record: ServiceRecord;
    serviceIntervals: ServiceIntervalMap;
    strings: DetailRowStrings;
}): DetailRowData[] => {
    // Discriminant narrowing: calibrations carry no part fields, and only
    // replaced/serviced records carry a used-part baseline.
    const partRecord = record.eventType === 'calibrated' ? undefined : record;
    const partTargetedRecord =
        record.eventType === 'replaced' || record.eventType === 'serviced' ? record : undefined;
    const partType = partRecord?.partType;
    const interval = partType ? serviceIntervals[partType] : undefined;
    const unitLabel = getUnitLabels(strings)[getUsageUnit({ interval, partType })];
    const partSerialNumber = partRecord?.partSerialNumber;
    const initialUsage = partTargetedRecord?.initialUsage;

    const rows: DetailRowData[] = [
        {
            label: strings.SERVICE_RECORD_PERFORMED_DATE,
            value: new Date(record.performedAt).toLocaleDateString()
        }
    ];

    if (partSerialNumber) {
        rows.push({ label: strings.SERVICE_RECORD_PART_SERIAL_NUMBER, value: partSerialNumber });
    }

    // Calibrated and inspected records stamp usageAtRecord as 0 (they touch no
    // counter — see getUsageAtRecord), so the snapshot is only shown where it's real.
    if (partTargetedRecord) {
        rows.push({
            label: strings.SERVICE_RECORD_USAGE_AT_SERVICE,
            value: `${partTargetedRecord.usageAtRecord} ${unitLabel}`
        });
    }

    if (initialUsage !== undefined) {
        rows.push({
            label: strings.SERVICE_RECORD_INITIAL_USAGE,
            value: `${initialUsage} ${unitLabel}`
        });
    }

    if (record.notes) {
        rows.push({ label: strings.SERVICE_RECORD_NOTES, value: record.notes });
    }

    return rows;
};
